import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';

/**
 * ConnectivityContext — Monitora estado de conexão do dispositivo.
 *
 * Usa navigator.onLine + eventos online/offline do browser.
 * Expõe isSyncing e pendingCount para integração com a fila offline.
 */

interface ConnectivityContextType {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  setSyncing(syncing: boolean): void;
  setPendingCount(count: number): void;
}

const ConnectivityContext = createContext<ConnectivityContextType | null>(null);

export function ConnectivityProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const setSyncingCb = useCallback((syncing: boolean) => {
    setIsSyncing(syncing);
  }, []);

  const setPendingCountCb = useCallback((count: number) => {
    setPendingCount(count);
  }, []);

  return (
    <ConnectivityContext.Provider
      value={{
        isOnline,
        isSyncing,
        pendingCount,
        setSyncing: setSyncingCb,
        setPendingCount: setPendingCountCb,
      }}
    >
      {children}
    </ConnectivityContext.Provider>
  );
}

export function useConnectivity(): ConnectivityContextType {
  const context = useContext(ConnectivityContext);
  if (!context) {
    throw new Error(
      'useConnectivity deve ser usado dentro de ConnectivityProvider',
    );
  }
  return context;
}
